// Reminder push copy, resolved at SEND time.
//
// Reminders used to store their text pre-translated: the client looked the
// template up in all three locales and wrote {pt, en, nl} into
// mirror_reminders.message. Two things follow from that, and both were shipping:
//
//   1. the dispatcher had no idea what language the user speaks, so with three
//      genuinely different strings in the payload OneSignal picked by DEVICE
//      locale — a Brazilian on an English phone got the English reminder;
//   2. whoever wrote the row froze the translation of that moment, so a user who
//      switched languages kept receiving the old one, and any copy fix reached
//      only reminders created after it.
//
// Now the row stores a KEY and the dispatcher renders it in the user's current
// language. The strings below are duplicated from
// src/i18n/locales/*.ts ("settings.reminders.template.*") because edge functions
// cannot import from src/ — the precedent is BRIEF_GREETINGS in agents-dispatch.
// The duplication is not on trust: src/test/reminderTemplates.test.ts fails the
// build if these drift from the locale files by a single character.

import type { LangKey } from "./lang.ts";

/** Categories that resolve to a canned, translatable string. */
export const REMINDER_TEMPLATE_KEYS = ["hydration", "movement", "motivation", "sleep"] as const;

export type ReminderTemplateKey = (typeof REMINDER_TEMPLATE_KEYS)[number];

export const REMINDER_TEMPLATES: Record<ReminderTemplateKey, Record<LangKey, string>> = {
  hydration: {
    pt: "Hora de beber água.",
    en: "Time to drink some water.",
    nl: "Tijd om wat water te drinken.",
    es: "Hora de beber agua.",
    fr: "C'est l'heure de boire de l'eau.",
    it: "È ora di bere acqua.",
    de: "Zeit, etwas Wasser zu trinken.",
    pl: "Czas napić się wody.",
    bg: "Време е за вода.",
  },
  movement: {
    pt: "Levanta e se mexe por um minuto.",
    en: "Stand up and move for a minute.",
    nl: "Sta op en beweeg even een minuut.",
    es: "Levántate y muévete un minuto.",
    fr: "Lève-toi et bouge une minute.",
    it: "Alzati e muoviti per un minuto.",
    de: "Steh auf und beweg dich eine Minute.",
    pl: "Wstań i poruszaj się minutę.",
    bg: "Изправи се и се раздвижи за минута.",
  },
  motivation: {
    pt: "Um passo pequeno agora. Você consegue.",
    en: "One small step now. You've got this.",
    nl: "Eén kleine stap nu. Je kunt dit.",
    es: "Un paso pequeño ahora. Tú puedes.",
    fr: "Un petit pas maintenant. Tu vas y arriver.",
    it: "Un piccolo passo adesso. Ce la fai.",
    de: "Ein kleiner Schritt jetzt. Du schaffst das.",
    pl: "Jeden mały krok teraz. Dasz radę.",
    bg: "Една малка стъпка сега. Можеш.",
  },
  sleep: {
    pt: "Hora de desacelerar pro sono.",
    en: "Time to wind down for sleep.",
    nl: "Tijd om tot rust te komen voor het slapen.",
    es: "Hora de bajar el ritmo para dormir.",
    fr: "C'est l'heure de ralentir pour dormir.",
    it: "È ora di rallentare per dormire.",
    de: "Zeit, für den Schlaf runterzukommen.",
    pl: "Czas wyciszyć się przed snem.",
    bg: "Време е да забавиш темпото за сън.",
  },
};

export function isReminderTemplateKey(value: unknown): value is ReminderTemplateKey {
  return typeof value === "string" &&
    (REMINDER_TEMPLATE_KEYS as readonly string[]).indexOf(value) !== -1;
}

/**
 * Render a stored reminder message for one user language.
 *
 * Three shapes are accepted, and the order matters:
 *   { key: "hydration" }  — current writers. Rendered fresh, every send.
 *   { text: "…" }         — free text the user typed. Never translatable; the
 *                           escape hatch that has to exist regardless.
 *   { pt, en, nl }        — legacy rows written before this change. Kept
 *                           working (26 of 27 live rows at the time), which is
 *                           why no data migration was needed. Picks the user's
 *                           language instead of leaving it to the device.
 *
 * Returns null when nothing renders, so the caller can skip instead of pushing
 * an empty notification.
 */
export function renderReminderMessage(
  message: Record<string, unknown> | null,
  langKey: LangKey,
): string | null {
  if (!message) return null;

  if (isReminderTemplateKey(message.key)) {
    return REMINDER_TEMPLATES[message.key][langKey];
  }

  if (typeof message.text === "string" && message.text.trim()) {
    return message.text.trim();
  }

  const legacy = (k: string): string =>
    typeof message[k] === "string" ? (message[k] as string).trim() : "";
  const pt = legacy("pt");
  const en = legacy("en");
  const nl = legacy("nl");
  if (!pt && !en && !nl) return null;
  if (langKey === "en") return en || pt || nl;
  if (langKey === "nl") return nl || en || pt;
  return pt || en || nl;
}
